import { Lock } from 'iconoir-react';
import React, { ChangeEvent, FC, ReactNode, Ref } from 'react';

import {
  INPUT_TYPES,
  POSITIONS,
} from '@savgroup-front-common/constants/src/shared';
import { MessageType } from '@savgroup-front-common/types';

import {
  safeFormattedIntlString,
  SafeFormattedMessage,
} from '../../../../formatters';
import { useIsNewUiEnabled } from '../../../../hooks/useIsNewUiEnabled';
import useScrollIntoView from '../../../../hooks/useScroll';
import { Tooltip } from '../../../Tooltip';
import { FieldMessages } from '../../common/helpers/getFinalFieldState.types';

import {
  $LockedSpan,
  $RadioDescription,
  $RadioInput,
  $RadioLabel,
  $RadioSpan,
  $Row,
} from './RadioButton.styles';

interface RadioNakedProps {
  isLocked?: boolean;
  danger?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  checked?: boolean;
  type?: 'checkbox' | 'radio';
  name: string;
  value?: string;
  dataTestId?: string;
  isRequired?: boolean;
}

export interface RadioProps extends React.PropsWithChildren<RadioNakedProps> {
  label?: MessageType | ReactNode;
  description?: string | ReactNode;
  errors?: FieldMessages;
  warnings?: FieldMessages;
  successes?: FieldMessages;
  isError?: boolean;
  isSuccess?: boolean;
  isWarning?: boolean;
  isFluid?: boolean;

  isLocked?: boolean;
  danger?: boolean;
  tabIndex?: number;

  selected?: boolean;
  isHighlighted?: boolean;
  isScrollIntoView?: boolean;
  onClick?: () => void;
  labelPosition?: POSITIONS;
  noRadioSpan?: boolean;
  isCompact?: boolean;
  lockedMessage?: MessageType;
}
interface RadioPropsWithRef extends RadioProps {
  forwardedRef: Ref<HTMLInputElement>;
}

const RadioButton: FC<React.PropsWithChildren<RadioPropsWithRef>> = ({
  name,
  tabIndex = 0,
  label = null,
  isFluid = false,
  dataTestId,
  forwardedRef,
  value,
  checked,
  isHighlighted = false,
  isScrollIntoView = false,
  onClick,
  description,
  noRadioSpan = false,
  isRequired = false,
  isCompact = false,
  isLocked = false,
  lockedMessage = undefined,
  children,
  ...rest
}) => {
  const { scrollIntoView } = useScrollIntoView({
    ref: forwardedRef,
    isScrollIntoView,
  });
  const isNewUiEnabled = useIsNewUiEnabled();

  const isLockedWithMessage = isLocked && !!lockedMessage;

  return (
    <$RadioLabel
      disabled={isLocked}
      $isFluid={isFluid || isNewUiEnabled}
      $isChecked={checked}
      $isNewUiEnabled={isNewUiEnabled}
      data-testid={dataTestId}
      isRequired={isRequired}
      $isCompact={isCompact}
    >
      <$Row>
        <div>
          <$RadioInput
            $isNewUiEnabled={isNewUiEnabled}
            $isChecked={checked}
            name={name}
            type={INPUT_TYPES.RADIO}
            tabIndex={tabIndex}
            ref={forwardedRef}
            value={value}
            checked={checked}
            isHighlighted={isHighlighted}
            disabled={isLocked}
            onClick={onClick}
            onFocus={() => {
              if (isScrollIntoView) {
                scrollIntoView();
              }
            }}
            {...rest}
          />

          {isLockedWithMessage && (
            <Tooltip content={safeFormattedIntlString(lockedMessage)}>
              <$LockedSpan>
                <Lock width={18} height={18} />
              </$LockedSpan>
            </Tooltip>
          )}

          {isLocked && !lockedMessage && (
            <$LockedSpan>
              <Lock width={18} height={18} />
            </$LockedSpan>
          )}

          {!noRadioSpan && !isLocked && (
            <$RadioSpan
              disabled={isLocked}
              $isChecked={checked}
              $hasDescription={!!description}
              $isNewUiEnabled={isNewUiEnabled}
              $isCompact={isCompact}
            />
          )}
        </div>
        <div>
          {label ? SafeFormattedMessage(label) : undefined}
          {description && <$RadioDescription>{description}</$RadioDescription>}
        </div>
        {children && <div>{children}</div>}
      </$Row>
    </$RadioLabel>
  );
};

export default React.forwardRef<HTMLInputElement, RadioProps>((props, ref) => (
  <RadioButton forwardedRef={ref} {...props} />
));
